import './button.scss';

import { kebabCase } from 'lodash';
import { MouseEvent, PropsWithChildren, useCallback } from 'react';

import { formatTestId } from '../../data/utils/string.utils';
import { Badge } from '../badge/badge';
import { Spinner } from '../spinner/spinner';

export enum ButtonType {
  DEFAULT = 'DEFAULT',
  PRIMARY = 'PRIMARY',
  SECONDARY = 'SECONDARY',
  SECONDARY_WHITE = 'SECONDARY_WHITE',
  TRANSPARENT = 'TRANSPARENT',
  WARNING = 'WARNING',
  TEXT = 'TEXT',
  ICON_ONLY = 'ICONONLY',
}

export enum ButtonIcon {
  NONE = 'NONE',
  LOADING = 'LOADING',
  CLOSE = 'CLOSE',
  CHEVRON = 'CHEVRON',
  CHEVRON_LEFT = 'CHEVRON_LEFT',
  CHEVRON_RIGHT = 'CHEVRON_RIGHT',
  DELETE = 'DELETE',
  DROPLET = 'DROPLET',
  PLUS = 'PLUS',
  MINUS = 'MINUS',
  COMMENT = 'COMMENT',
  COMMENTS = 'COMMENTS',
  CLOCK = 'CLOCK',
  CALENDAR = 'CALENDAR',
  PAPERPLANE = 'PAPER_PLANE',
  ELLIPSIS_VERTICAL = 'ELLIPSIS_VERTICAL',
  CHECK = 'CHECK',
  DOWNLOAD = 'DOWNLOAD',
  UPLOAD = 'UPLOAD',
  INFO = 'INFO',
  CALENDAR_ADD = 'CALENDAR_ADD',
  CALENDAR_EXCLAMATION = 'CALENDAR_EXCLAMATION',
  CALENDAR_CANCEL = 'CALENDAR_CANCEL',
  CALENDAR_SEARCH = 'CALENDAR_SEARCH',
  LIST_ADD = 'LIST_ADD',
  USER_ADD = 'USER_ADD',
  USER_REMOVE = 'USER_REMOVE',
  USER_GROUP = 'USER_GROUP',
  CHAT_ADD = 'CHAT_ADD',
  REMINDER_ADD = 'REMINDER_ADD',
  EDIT = 'EDIT',
  LOCK = 'LOCK',
  LOCK_OPEN = 'LOCK_OPEN',
  SORT_ASC = 'SORT_ASC',
  SORT_DESC = 'SORT_DESC',
  THUMBS_UP = 'THUMBS_UP',
  THUMBS_DOWN = 'THUMBS_DOWN',
}

export type ButtonProps = PropsWithChildren<{
  testId?: string;
  useAsSubmit?: boolean;
  type?: ButtonType;
  icon?: ButtonIcon;
  active?: boolean;
  inverted?: boolean;
  toggle?: boolean;
  disabled?: boolean;
  className?: string;
  badge?: number;
  onClick?: (e: MouseEvent<HTMLElement>) => void;
}>;

export function Button({
  testId,
  useAsSubmit = false,
  type = ButtonType.DEFAULT,
  icon = ButtonIcon.NONE,
  active = false,
  inverted = false,
  toggle = false,
  disabled = false,
  badge,
  className,
  onClick,
  ...props
}: ButtonProps) {
  const getTestId = useCallback(
    (testIdType: string | string[] | null = null) =>
      formatTestId('button', testId, testIdType),
    [testId]
  );

  const onHandleClick = useCallback(
    (e: MouseEvent<HTMLElement>) => {
      e.preventDefault();
      e.stopPropagation();

      onClick?.(e);
    },
    [onClick]
  );

  return (
    <button
      data-test-id={getTestId()}
      type={useAsSubmit ? 'submit' : 'button'}
      className={`button ${kebabCase(type)}${inverted ? ` inverted` : ``}${
        active ? ` active` : ``
      }${className ? ` button--${className}` : ``}`}
      disabled={disabled}
      onClick={onHandleClick}
    >
      {icon === ButtonIcon.LOADING && <Spinner />}

      {icon !== ButtonIcon.NONE && icon !== ButtonIcon.LOADING && (
        <i
          data-test-id={getTestId('icon')}
          className={`icon ${kebabCase(icon)}`}
        />
      )}

      {props.children && (
        <span data-test-id={getTestId('text')}>{props.children}</span>
      )}

      {toggle && <i className="toggle" />}
      {badge !== undefined && badge > 0 && <Badge value={badge.toString()} />}
    </button>
  );
}
